<script lang="ts">
  import { onMount } from "svelte";
  import { resizableColumns } from "$lib/columnWidths.svelte";
  import { _ } from "svelte-i18n";
  import SectionGuide from "$components/SectionGuide.svelte";
  import RowActions from "$components/RowActions.svelte";
  import { ApiError } from "$lib/api";
  import {
    createCategory,
    deleteCategory,
    fetchCategories,
    reorderCategories,
    updateCategory,
    type Category,
  } from "$lib/catalog";

  /* «Категории товаров» is the first tab of Продукты since 2026-09-16 — the
     tree and the cards filed in it are one screen — so this is a component
     with a slug, not a page with its own shell. */
  let { slug }: { slug: string } = $props();

  let categories = $state<Category[]>([]);
  let loading = $state(true);
  let saving = $state(false);
  let error = $state("");
  let saved = $state("");
  let editTarget = $state<Category | null>(null);
  let formOpen = $state(false);
  let formError = $state("");

  // Add / full-edit form state
  let formName = $state("");
  let formSlug = $state("");
  let formParent = $state("");
  let formPosition = $state("0");
  let formActive = $state(true);

  // Inline name-edit state
  let inlineEditId = $state<number | null>(null);
  let inlineEditValue = $state("");

  // Delete-with-detach modal state
  let deleteTarget = $state<Category | null>(null);
  let deleteProductCount = $state(0);
  let deleteConfirming = $state(false);

  const parentOptions = $derived(
    categories.filter((cat) => !editTarget || cat.id !== editTarget.id),
  );

  async function load() {
    loading = true;
    error = "";
    try {
      categories = await fetchCategories(slug);
    } catch (e: any) {
      error = e?.message ?? $_("categories.error.load");
    } finally {
      loading = false;
    }
  }

  function slugify(value: string): string {
    return value
      .toLowerCase()
      .normalize("NFKD")
      .replace(/[̀-ͯ]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 120);
  }

  function resetForm() {
    editTarget = null;
    formName = "";
    formSlug = "";
    formParent = "";
    formPosition = "0";
    formActive = true;
    formError = "";
  }

  function openCreateModal() {
    resetForm();
    inlineEditId = null;
    error = "";
    saved = "";
    formOpen = true;
  }

  function closeFormModal() {
    if (saving) return;
    formOpen = false;
    resetForm();
  }

  function startEdit(cat: Category) {
    editTarget = cat;
    formName = cat.name;
    formSlug = cat.slug;
    formParent = cat.parent_id == null ? "" : String(cat.parent_id);
    formPosition = String(cat.position);
    formActive = cat.is_active;
    inlineEditId = null; // close any inline edit
    error = "";
    formError = "";
    saved = "";
    formOpen = true;
  }

  function onNameInput() {
    if (!editTarget) formSlug = slugify(formName);
  }

  async function submit() {
    if (!formName.trim()) {
      formError = $_("categories.error.nameRequired");
      return;
    }
    const payload = {
      name: formName.trim(),
      slug: (formSlug.trim() || slugify(formName)).slice(0, 120),
      parent_id: formParent ? Number(formParent) : null,
      position: Number(formPosition) || 0,
      is_active: formActive,
    };
    saving = true;
    formError = "";
    saved = "";
    try {
      const successMessage = editTarget
        ? $_("categories.saved.updated")
        : $_("categories.saved.created");
      if (editTarget) {
        await updateCategory(slug, editTarget.id, payload);
      } else {
        await createCategory(slug, payload);
      }
      formOpen = false;
      resetForm();
      await load();
      saved = successMessage;
    } catch (e: any) {
      formError = e instanceof ApiError ? e.message : $_("categories.error.save");
    } finally {
      saving = false;
    }
  }

  // ---------------------------------------------------------------------------
  // Inline name editing (click name cell → input → Enter/Esc)
  // ---------------------------------------------------------------------------

  function startInlineEdit(cat: Category) {
    formOpen = false;
    resetForm();
    inlineEditId = cat.id;
    inlineEditValue = cat.name;
  }

  function cancelInlineEdit() {
    inlineEditId = null;
    inlineEditValue = "";
  }

  async function saveInlineName(cat: Category) {
    const trimmed = inlineEditValue.trim();
    if (!trimmed || trimmed === cat.name) { cancelInlineEdit(); return; }
    try {
      await updateCategory(slug, cat.id, { name: trimmed });
      saved = $_("categories.saved.updated");
      setTimeout(() => (saved = ""), 2500);
      cancelInlineEdit();
      await load();
    } catch (e: any) {
      error = e instanceof ApiError ? e.message : $_("categories.error.save");
    }
  }

  /** Focus the inline rename input when it appears (replaces `autofocus`,
   *  which screen readers handle poorly). */
  function focusOnMount(node: HTMLInputElement) {
    node.focus();
  }

  function onInlineKeydown(e: KeyboardEvent, cat: Category) {
    if (e.key === "Enter") { e.preventDefault(); saveInlineName(cat); }
    if (e.key === "Escape") { e.preventDefault(); cancelInlineEdit(); }
  }

  // ---------------------------------------------------------------------------
  // Delete with detach flow
  // ---------------------------------------------------------------------------

  async function requestDelete(cat: Category) {
    deleteTarget = cat;
    deleteProductCount = cat.product_count;
    if (cat.product_count === 0) {
      // No products → delete immediately
      await confirmDelete(false);
    }
    // else: modal will show
  }

  async function confirmDelete(detach: boolean) {
    if (!deleteTarget) return;
    deleteConfirming = true;
    error = "";
    try {
      await deleteCategory(slug, deleteTarget.id, detach);
      saved = $_("categories.saved.deleted");
      setTimeout(() => (saved = ""), 2500);
      if (editTarget?.id === deleteTarget.id) {
        formOpen = false;
        resetForm();
      }
      deleteTarget = null;
      await load();
    } catch (e: any) {
      error = e instanceof ApiError ? e.message : $_("categories.error.delete");
      deleteTarget = null;
    } finally {
      deleteConfirming = false;
    }
  }

  function cancelDelete() { deleteTarget = null; }

  // ---------------------------------------------------------------------------
  // Reordering (explicit up/down within a sibling group)
  // ---------------------------------------------------------------------------

  let reordering = $state(false);

  /* Siblings in the exact order the table draws them — the raw API order.
     The arrows and the swap used to work on a privately re-sorted copy
     (position, then `localeCompare` on the name). The DB breaks position
     ties with its own collation, so the two orders disagreed on any
     mixed-alphabet or untouched (all-position-0) group: middle rows on
     screen were first/last in the hidden list and their arrows went dead.
     One ordering, shared by rendering, enabling and moving, ends that. */
  function orderedSiblings(parentId: number | null): Category[] {
    return categories.filter((c) => (c.parent_id ?? null) === (parentId ?? null));
  }

  /** Move a category one slot up/down within its sibling group and persist. */
  async function move(cat: Category, dir: -1 | 1) {
    if (reordering) return;
    const siblings = [...orderedSiblings(cat.parent_id ?? null)];
    const idx = siblings.findIndex((c) => c.id === cat.id);
    const target = idx + dir;
    if (idx < 0 || target < 0 || target >= siblings.length) return;
    [siblings[idx], siblings[target]] = [siblings[target], siblings[idx]];
    reordering = true;
    error = "";
    try {
      categories = await reorderCategories(slug, siblings.map((c) => c.id));
      saved = $_("categories.saved.reordered");
      setTimeout(() => (saved = ""), 2000);
    } catch (e: any) {
      error = e instanceof ApiError ? e.message : $_("categories.error.save");
      await load();
    } finally {
      reordering = false;
    }
  }

  function siblingIndex(cat: Category): { idx: number; last: number } {
    const siblings = orderedSiblings(cat.parent_id ?? null);
    return { idx: siblings.findIndex((c) => c.id === cat.id), last: siblings.length - 1 };
  }

  function parentName(parentId: number | null): string {
    if (parentId == null) return $_("categories.noParent");
    return categories.find((cat) => cat.id === parentId)?.name ?? $_("categories.noParent");
  }

  onMount(() => { if (slug) load(); });
</script>

<div class="cat-page">
    <!-- The page title is «Продукты» now; what is left of the old header is
         what this tab needs: the guide «?» and the one button. -->
    <div class="page-heading">
      <div class="title-row">
        <h2 class="cat-title">{$_("categories.title")}</h2>
        <SectionGuide id="categories" />
      </div>
      <button type="button" class="btn btn--primary" onclick={openCreateModal}>
        <span class="material-icons-outlined" aria-hidden="true">add</span>
        {$_("categories.form.create")}
      </button>
    </div>

    {#if error}
      <div class="error-banner">{error}</div>
    {/if}
    {#if saved}
      <div class="success-banner">{saved}</div>
    {/if}

    <section class="card table-card">
      {#if loading}
        <div class="empty-state">{$_("common.loading")}</div>
      {:else if categories.length === 0}
        <div class="empty-state empty-state--onboarding">
          <span class="material-icons-outlined empty-icon" aria-hidden="true">account_tree</span>
          <p class="empty-title">{$_("categories.empty")}</p>
          <p class="empty-desc">{$_("categories.empty.desc")}</p>
          <button type="button" class="btn btn--primary" onclick={openCreateModal}>
            <span class="material-icons-outlined" aria-hidden="true">add</span>
            {$_("categories.empty.addGroup")}
          </button>
        </div>
      {:else}
        <div class="table-scroll">
          <table use:resizableColumns={"categories"} class="cat-table">
            <thead>
              <tr>
                <th>{$_("categories.col.name")}</th>
                <th>{$_("categories.col.slug")}</th>
                <th>{$_("categories.col.parent")}</th>
                <th class="num">{$_("categories.col.products")}</th>
                <th>{$_("categories.col.order")}</th>
                <th>{$_("categories.col.status")}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {#each categories.filter(c => !c.parent_id) as root}
                {@const rootSi = siblingIndex(root)}
                <tr class:editing={inlineEditId === root.id}>
                  <td class="td-name">
                    {#if inlineEditId === root.id}
                      <input
                        class="inline-input"
                        type="text"
                        bind:value={inlineEditValue}
                        onkeydown={(e) => onInlineKeydown(e, root)}
                        onblur={() => saveInlineName(root)}
                        use:focusOnMount
                      />
                    {:else}
                      <span
                        class="name-link"
                        onclick={() => startEdit(root)}
                        role="link"
                        tabindex="0"
                        onkeydown={(e) => e.key === "Enter" && startEdit(root)}
                      >{root.name}</span>
                      <button
                        type="button"
                        class="rename-btn"
                        title={$_("categories.inlineEditHint")}
                        aria-label={$_("categories.inlineEditHint")}
                        onclick={() => startInlineEdit(root)}
                      >
                        <span class="material-icons-outlined" aria-hidden="true">edit</span>
                      </button>
                    {/if}
                  </td>
                  <td class="td-slug">{root.slug}</td>
                  <td>{$_("categories.noParent")}</td>
                  <td class="num">{root.product_count}</td>
                  <td class="td-order">
                    <button type="button" class="reorder-btn" title={$_("categories.reorder.up")}
                      disabled={reordering || rootSi.idx <= 0} onclick={() => move(root, -1)}>
                      <span class="material-icons-outlined" aria-hidden="true">arrow_upward</span>
                    </button>
                    <button type="button" class="reorder-btn" title={$_("categories.reorder.down")}
                      disabled={reordering || rootSi.idx >= rootSi.last} onclick={() => move(root, 1)}>
                      <span class="material-icons-outlined" aria-hidden="true">arrow_downward</span>
                    </button>
                  </td>
                  <td>
                    <span class="status-tag" class:inactive={!root.is_active}>
                      {root.is_active ? $_("common.active") : $_("common.inactive")}
                    </span>
                  </td>
                  <td class="td-actions">
                    <RowActions
                      onedit={() => startEdit(root)}
                      ondelete={() => requestDelete(root)}
                      deleteTitle={$_("dp.table.action.delete")}
                    />
                  </td>
                </tr>
                {#each categories.filter(c => c.parent_id === root.id) as sub}
                  {@const subSi = siblingIndex(sub)}
                  <tr class="tr-sub" class:editing={inlineEditId === sub.id}>
                    <td class="td-name">
                      <span class="indent">↳</span>
                      {#if inlineEditId === sub.id}
                        <input
                          class="inline-input"
                          type="text"
                          bind:value={inlineEditValue}
                          onkeydown={(e) => onInlineKeydown(e, sub)}
                          onblur={() => saveInlineName(sub)}
                          use:focusOnMount
                        />
                      {:else}
                        <span
                          class="name-link"
                          onclick={() => startEdit(sub)}
                          role="link"
                          tabindex="0"
                          onkeydown={(e) => e.key === "Enter" && startEdit(sub)}
                        >{sub.name}</span>
                        <button
                          type="button"
                          class="rename-btn"
                          title={$_("categories.inlineEditHint")}
                          aria-label={$_("categories.inlineEditHint")}
                          onclick={() => startInlineEdit(sub)}
                        >
                          <span class="material-icons-outlined" aria-hidden="true">edit</span>
                        </button>
                      {/if}
                    </td>
                    <td class="td-slug">{sub.slug}</td>
                    <td>{root.name}</td>
                    <td class="num">{sub.product_count}</td>
                    <td class="td-order">
                      <button type="button" class="reorder-btn" title={$_("categories.reorder.up")}
                        disabled={reordering || subSi.idx <= 0} onclick={() => move(sub, -1)}>
                        <span class="material-icons-outlined" aria-hidden="true">arrow_upward</span>
                      </button>
                      <button type="button" class="reorder-btn" title={$_("categories.reorder.down")}
                        disabled={reordering || subSi.idx >= subSi.last} onclick={() => move(sub, 1)}>
                        <span class="material-icons-outlined" aria-hidden="true">arrow_downward</span>
                      </button>
                    </td>
                    <td>
                      <span class="status-tag" class:inactive={!sub.is_active}>
                        {sub.is_active ? $_("common.active") : $_("common.inactive")}
                      </span>
                    </td>
                    <td class="td-actions">
                      <RowActions
                        onedit={() => startEdit(sub)}
                        ondelete={() => requestDelete(sub)}
                        deleteTitle={$_("dp.table.action.delete")}
                      />
                    </td>
                  </tr>
                {/each}
              {/each}
            </tbody>
          </table>
        </div>
      {/if}
    </section>
</div>

<!-- Add / edit category modal -->
{#if formOpen}
  <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
  <div class="modal-overlay" role="presentation" onclick={closeFormModal}>
    <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
    <div
      class="category-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="category-modal-title"
      tabindex="-1"
      onclick={(e) => e.stopPropagation()}
    >
      <div class="category-modal__header">
        <div class="category-modal__title-wrap">
          <span class="material-icons-outlined modal-icon" aria-hidden="true">category</span>
          <h2 id="category-modal-title" class="modal-title">
            {editTarget ? $_("categories.form.editTitle") : $_("categories.form.addTitle")}
          </h2>
        </div>
        <button type="button" class="modal-close" onclick={closeFormModal} aria-label={$_("common.close")}>
          <span class="material-icons-outlined" aria-hidden="true">close</span>
        </button>
      </div>

      {#if formError}
        <div class="modal-error">{formError}</div>
      {/if}

      <form
        class="category-modal__body"
        onsubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <div class="form-grid">
          <div class="field">
            <label class="label" for="cat-name">{$_("categories.field.name")}</label>
            <input
              id="cat-name"
              class="text-input"
              type="text"
              bind:value={formName}
              oninput={onNameInput}
              placeholder={$_("categories.field.name.placeholder")}
            />
          </div>
          <div class="field">
            <label class="label" for="cat-slug">{$_("categories.field.slug")}</label>
            <input
              id="cat-slug"
              class="text-input"
              type="text"
              bind:value={formSlug}
              placeholder={$_("categories.field.slug.placeholder")}
            />
          </div>
          <div class="field">
            <label class="label" for="cat-parent">{$_("categories.field.parent")}</label>
            <select id="cat-parent" class="text-input" bind:value={formParent}>
              <option value="">{$_("categories.noParent")}</option>
              {#each parentOptions as cat}
                <option value={String(cat.id)}>{cat.name}</option>
              {/each}
            </select>
          </div>
          <div class="field">
            <label class="label" for="cat-position">{$_("categories.field.position")}</label>
            <input
              id="cat-position"
              class="text-input"
              type="number"
              min="0"
              step="1"
              bind:value={formPosition}
            />
          </div>
          <div class="field field--checkbox">
            <label class="label" for="cat-active">
              <input id="cat-active" type="checkbox" bind:checked={formActive} />
              {$_("categories.field.isActive")}
            </label>
          </div>
        </div>

        <div class="category-modal__footer">
          <button type="button" class="btn btn--ghost" onclick={closeFormModal} disabled={saving}>
            {$_("common.cancel")}
          </button>
          <button type="submit" class="btn btn--primary" disabled={saving}>
            {saving
              ? $_("common.saving")
              : editTarget
                ? $_("common.save")
                : $_("categories.form.create")}
          </button>
        </div>
      </form>
    </div>
  </div>
{/if}

<!-- Delete-with-detach confirmation modal -->
{#if deleteTarget && deleteProductCount > 0}
  <div class="modal-overlay" role="dialog" aria-modal="true">
    <div class="modal-box">
      <h2 class="modal-title">{$_("categories.deleteModal.title")}</h2>
      <p class="modal-body">
        {$_("categories.deleteModal.body", {
          values: { name: deleteTarget.name, count: deleteProductCount },
        })}
      </p>
      <p class="modal-hint">{$_("categories.deleteModal.hint")}</p>
      <div class="modal-actions">
        <button class="btn btn--ghost btn--sm" onclick={cancelDelete} disabled={deleteConfirming}>
          {$_("common.cancel")}
        </button>
        <button
          class="btn btn--danger"
          onclick={() => confirmDelete(true)}
          disabled={deleteConfirming}
        >
          {deleteConfirming ? $_("common.deleting") : $_("categories.deleteModal.confirm")}
        </button>
      </div>
    </div>
  </div>
{/if}

<style>
  .cat-page { display: flex; flex-direction: column; gap: 16px; }
  .page-heading { display: flex; justify-content: space-between; align-items: center; gap: 16px; }
  .cat-title { margin: 0; font-size: 17px; font-weight: 700; color: var(--color-surface-darker); }

  .card { background: #fff; border: 1px solid var(--color-divider); border-radius: var(--radius); }
  .form-grid { display: grid; grid-template-columns: repeat(2, minmax(180px, 1fr)); gap: 14px; }
  @media (max-width: 720px) { .page-heading { flex-direction: column; align-items: stretch; } }
  @media (max-width: 640px) { .form-grid { grid-template-columns: 1fr; } }

  .field { display: flex; flex-direction: column; gap: 5px; }
  .label { font-size: 13px; font-weight: 600; color: var(--color-surface-darker); }
  .text-input { width: 100%; box-sizing: border-box; padding: 8px 10px; border: 1px solid var(--color-divider); border-radius: var(--radius); font-size: 13px; background: #fff; }
  .text-input:focus { outline: none; border-color: var(--color-primary); }

  .field--checkbox { grid-column: 1 / -1; }
  .field--checkbox .label { display: flex; align-items: center; gap: 8px; cursor: pointer; }
  .field--checkbox input { width: 18px; height: 18px; }

  .status-tag { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; background: #e8f5e9; color: #2e7d32; }
  .status-tag.inactive { background: #f5f5f5; color: #9e9e9e; }

  .error-banner { background: #fdecea; border: 1px solid #f5c6cb; padding: 12px 16px; border-radius: var(--radius); color: #c0392b; font-size: 14px; }
  .success-banner { background: #e8f5e9; border: 1px solid #a5d6a7; padding: 12px 16px; border-radius: var(--radius); color: #2e7d32; font-size: 14px; }

  .table-card { overflow: hidden; }
  .table-scroll { overflow-x: auto; }
  .empty-state { padding: 48px; text-align: center; color: var(--color-text-muted); font-size: 14px; }
  .empty-state--onboarding { padding: 72px 32px; display: flex; flex-direction: column; align-items: center; gap: 12px; }
  .empty-icon { font-size: 48px !important; opacity: 0.4; }
  .empty-title { font-size: 16px; font-weight: 700; color: var(--color-surface-darker); margin: 0; }
  .empty-desc { margin: 0; max-width: 420px; }
  .empty-state--onboarding .btn { margin-top: 8px; display: inline-flex; align-items: center; gap: 6px; }
  .cat-table { width: 100%; border-collapse: collapse; font-size: 13px; }
  .cat-table th { padding: 10px 14px; text-align: left; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: var(--color-text-muted); background: var(--color-bg); border-bottom: 1px solid var(--color-divider); white-space: nowrap; }
  .cat-table td { padding: 11px 14px; border-bottom: 1px solid var(--color-divider); vertical-align: middle; }
  .cat-table tr:last-child td { border-bottom: none; }
  .cat-table tr:hover td { background: rgba(33,133,213,0.03); }
  .cat-table tr.editing td { background: #f0f7ff; }
  .tr-sub td { background: #fafafa; }
  .tr-sub.editing td { background: #f0f7ff; }
  .indent { color: var(--color-divider); margin-right: 8px; font-weight: normal; }
  .td-name { font-weight: 600; color: var(--color-surface-darker); }
  .td-slug { font-family: monospace; color: var(--color-text-muted); }
  .td-actions { display: flex; gap: 6px; justify-content: flex-end; white-space: nowrap; }
  .td-order { white-space: nowrap; }
  .cat-table th.num, .cat-table td.num { text-align: right; font-variant-numeric: tabular-nums; }
  .reorder-btn { display: inline-flex; align-items: center; justify-content: center; width: 26px; height: 26px; padding: 0; border: 1px solid var(--color-divider); border-radius: var(--radius-sm); background: #fff; color: var(--color-primary); cursor: pointer; }
  .reorder-btn + .reorder-btn { margin-left: 4px; }
  .reorder-btn:hover:not(:disabled) { background: var(--color-primary); color: #fff; border-color: var(--color-primary); }
  .reorder-btn:disabled { opacity: 0.35; cursor: default; }
  .reorder-btn .material-icons-outlined { font-size: 16px; }

  /* Inline name editing */
  /* Category name is a link to the Edit modal; the pencil button keeps inline rename. */
  .name-link { cursor: pointer; color: var(--color-primary); border-radius: 3px; padding: 2px 4px; margin: -2px -4px; transition: background 0.1s; }
  .name-link:hover { text-decoration: underline; background: rgba(33,133,213,0.08); }
  .name-link:focus-visible { outline: 2px solid var(--color-primary); outline-offset: 1px; }
  .rename-btn { border: none; background: none; cursor: pointer; padding: 2px; margin-left: 6px;
    color: var(--color-text-muted); border-radius: 4px; vertical-align: middle; opacity: 0; transition: opacity 0.1s, color 0.1s; }
  .rename-btn:hover { color: var(--color-primary); background: rgba(33,133,213,0.08); }
  .rename-btn .material-icons-outlined { font-size: 16px; }
  tr:hover .rename-btn, tr.tr-sub:hover .rename-btn { opacity: 1; }
  .rename-btn:focus-visible { opacity: 1; outline: 2px solid var(--color-primary); outline-offset: 1px; }
  .inline-input { border: 1px solid var(--color-primary); border-radius: 4px; padding: 4px 8px; font-size: 13px; font-weight: 600; width: 100%; box-sizing: border-box; outline: none; box-shadow: 0 0 0 3px rgba(33,133,213,0.15); }

  .btn { display: inline-flex; align-items: center; justify-content: center; gap: 6px; padding: 8px 16px; border-radius: var(--radius); border: none; cursor: pointer; font-size: 13px; font-weight: 600; }
  .btn:disabled { opacity: 0.55; cursor: default; }
  .btn .material-icons-outlined { font-size: 17px; line-height: 1; }
  .btn--primary { background: var(--color-primary); color: #fff; }
  .btn--ghost { background: transparent; color: var(--color-primary); border: 1px solid var(--color-primary); }
  .btn--danger { background: #dc2626; color: #fff; }
  .btn--danger:hover:not(:disabled) { background: #b91c1c; }
  .btn--sm { padding: 6px 12px; font-size: 12px; }

  /* Category form modal */
  .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.45); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 20px; }
  .category-modal { background: #fff; border-radius: 8px; width: 100%; max-width: 680px; max-height: calc(100vh - 40px); display: flex; flex-direction: column; box-shadow: 0 20px 60px rgba(0,0,0,0.22); overflow: hidden; }
  .category-modal__header { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 18px 24px; border-bottom: 1px solid var(--color-divider); flex-shrink: 0; }
  .category-modal__title-wrap { display: flex; align-items: center; gap: 10px; min-width: 0; }
  .category-modal .modal-title { margin: 0; }
  .modal-icon { font-size: 21px; color: var(--color-primary); }
  .modal-close { display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 32px; padding: 0; border: none; border-radius: var(--radius-sm); background: transparent; color: var(--color-text-muted); cursor: pointer; flex-shrink: 0; }
  .modal-close:hover { background: var(--color-bg); color: var(--color-surface-darker); }
  .modal-close .material-icons-outlined { font-size: 20px; }
  .modal-error { margin: 16px 24px 0; background: #fdecea; border: 1px solid #f5c6cb; padding: 10px 12px; border-radius: var(--radius); color: #c0392b; font-size: 13px; }
  .category-modal__body { padding: 20px 24px 0; overflow-y: auto; }
  .category-modal__footer { display: flex; justify-content: flex-end; gap: 10px; margin: 20px -24px 0; padding: 16px 24px; border-top: 1px solid var(--color-divider); background: #fff; }

  /* Delete modal */
  .modal-box { background: #fff; border-radius: 8px; padding: 28px 32px; max-width: 440px; width: 90%; box-shadow: 0 8px 32px rgba(0,0,0,0.18); }
  .modal-title { margin: 0 0 12px; font-size: 18px; font-weight: 700; color: var(--color-surface-darker); }
  .modal-body { margin: 0 0 8px; font-size: 14px; line-height: 1.5; }
  .modal-hint { margin: 0 0 24px; font-size: 12px; color: var(--color-text-muted); }
  .modal-actions { display: flex; gap: 10px; justify-content: flex-end; }
</style>
